import { signatureImageKind } from '../../../utils/emailBodyHtml';

const MAX_DEFAULT_WIDTH_PX = 300;

/** Uploads a signature image; returns the public URL to reference in the signature HTML. */
export async function uploadSignatureImage(file: Blob, filename = 'bild'): Promise<string> {
  const form = new FormData();
  form.append('file', file, filename);
  const res = await fetch('/api/inbox/signature-images', {
    method: 'POST',
    body: form,
    credentials: 'include',
  });
  const data = (await res.json().catch(() => null)) as { url?: string; error?: string } | null;
  if (!res.ok || !data?.url) throw new Error(data?.error || 'Bild konnte nicht hochgeladen werden.');
  return data.url;
}

function dataUrlToBlob(dataUrl: string): Blob | null {
  const match = /^data:(image\/(?:png|jpe?g|gif|webp));base64,([\s\S]+)$/i.exec(dataUrl.trim());
  if (!match) return null;
  try {
    const bin = atob(match[2].replace(/\s+/g, ''));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: match[1].toLowerCase() });
  } catch {
    return null;
  }
}

/** Natural size of an image file, scaled down to a sensible signature width. */
export async function signatureImageDisplayWidth(file: Blob): Promise<number | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const width = bitmap.width;
    bitmap.close();
    return width > 0 ? Math.min(width, MAX_DEFAULT_WIDTH_PX) : null;
  } catch {
    return null;
  }
}

/**
 * Before sanitizing pasted HTML: uploads embedded data: images (Outlook/Word) and counts the
 * images recipients could never load (e.g. Gmail attachment views) — those get dropped.
 */
export async function preparePastedSignatureHtml(
  html: string,
): Promise<{ html: string; droppedImages: number; uploadErrors: string[] }> {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  let droppedImages = 0;
  const uploadErrors: string[] = [];
  for (const img of Array.from(doc.querySelectorAll('img'))) {
    const src = img.getAttribute('src') ?? '';
    const alt = img.getAttribute('alt') || img.getAttribute('title') || '';
    if (signatureImageKind(src, alt) !== 'unusable') continue;
    const blob = src.startsWith('data:') ? dataUrlToBlob(src) : null;
    if (!blob) {
      droppedImages += 1;
      continue;
    }
    try {
      img.setAttribute('src', await uploadSignatureImage(blob));
    } catch (e) {
      uploadErrors.push(e instanceof Error ? e.message : String(e));
      droppedImages += 1;
    }
  }
  return { html: doc.body.innerHTML, droppedImages, uploadErrors };
}
